import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NoteType } from '@app/shared/components/note/note.interface';
import { BDCProperty } from '@app/shared/interfaces/lib/bdc-property.interface';
import { BookingFlowService } from '@app/shared/services/booking-flow/booking-flow.service';
import { ConfirmationService } from '@app/shared/services/confirmation/confirmation.service';
import { SettingsService } from '@app/shared/services/settings/settings.service';

@Component({
  standalone: false,
  selector: 'sbnb-connect-booking-review-properties',
  templateUrl: './connect-booking-review-properties.component.html',
  styleUrls: ['./connect-booking-review-properties.component.scss'],
})
export class ConnectBookingReviewPropertiesComponent implements OnInit {
  NoteType = NoteType;
  loading: Boolean = false;
  reloading: Boolean = false;
  properties: BDCProperty[] = [];

  @Input() propertyIds: any[];
  @Input() quickSetup: boolean = false;
  @Output() propertiesEmitter: EventEmitter<any> = new EventEmitter();
  @Output() noPropertyIdsEmitter: EventEmitter<any> = new EventEmitter();

  connectedProperties: BDCProperty[] = [];
  ineligibleProperties: BDCProperty[] = [];
  unmatchedPropertyIds: any[] = [];
  retriedTimes: number = 0;

  constructor(
    private readonly settingsService: SettingsService,
    private readonly router: Router,
    private confirmationService: ConfirmationService,
    private bookingFlowService: BookingFlowService
  ) {
    this.confirmationService.setConfirmation(
      'Are you sure?\nYour properties will not be connected if you exit now.',
      () => {
        return confirm('Are you sure?\nYour properties will not be connected if you exit now.');
      }
    );
  }

  ngOnInit(): void {
    if (this.propertyIds.length === 0) {
      if (this.bookingFlowService.propertyIds.length > 0) {
        this.propertyIds = this.bookingFlowService.propertyIds;
      }
    }

    if (this.propertyIds.length === 0) {
      this.noPropertyIdsEmitter.emit();
      return;
    }

    this.connectProperties(this.propertyIds);
  }

  connectProperties(propertyIds: any[]) {
    if (!this.reloading) {
      this.loading = true;
    }

    const email = localStorage.getItem('account_login');
    if (!email) {
      return;
    }

    // Connect the properties using the new Connectivity APIs
    this.settingsService.connectProperties(propertyIds, 'booking', email, this.quickSetup).subscribe((res) => {
      this.loading = false;

      if (res.error && res.error.status !== 422) {
        if (this.retriedTimes == 0) {
          this.retriedTimes++;
          this.connectProperties(propertyIds);
        }
        return;
      }

      if (res.error) {
        return;
      }

      if (Array.isArray(res.data)) {
        this.properties = [...this.properties, ...res.data];
      }

      this.processProperties();
      this.filterUnmatchedPropertyIds();

      if (this.unmatchedPropertyIds.length > 0 && this.retriedTimes < 3) {
        this.reloading = true;
        this.retriedTimes++;
        this.connectProperties(this.unmatchedPropertyIds);
      } else {
        this.reloading = false;
      }
    });
  }

  processProperties() {
    this.connectedProperties = this.properties.filter((property) => property.issues.length === 0);
    this.ineligibleProperties = this.properties.filter((property) => property.issues.length > 0);
  }

  filterUnmatchedPropertyIds() {
    this.unmatchedPropertyIds = this.propertyIds.filter(
      (propertyId) => !this.properties.some((property) => property.id == propertyId)
    );
  }

  progress() {
    if (this.connectedProperties.length > 0) {
      this.propertiesEmitter.emit(this.connectedProperties.map((property) => property.id));
    } else {
      this.router.navigate(['/accounts']);
    }
  }

  back() {
    window.history.back();
  }

  ngOnDestroy() {
    this.confirmationService.clearConfirmation();
  }
}
